import { computed } from 'vue'

import {
  CopyrightOwnerType,
  useCopyrightOwnersNewQuery,
  type CopyrightOwnersNewQuery,
} from '@/shared/graphql/legacy/generated'
import { openDialog, Dialog } from '@/shared/dialogs/dialog'
import { openModal, Modals } from '@/shared/dialogs/modals'

export type CopyrightOwnerItem =
  CopyrightOwnersNewQuery['copyrightOwnersNew']['items'][number]

export function useCopyrightOwners() {
  const { result, fetchMore, loading } = useCopyrightOwnersNewQuery()

  const owners = computed(() => result.value?.copyrightOwnersNew.items ?? [])

  const isAccount = (owner: CopyrightOwnerItem) =>
    owner.type === CopyrightOwnerType.Account

  const isOrganization = (owner: CopyrightOwnerItem) =>
    owner.type === CopyrightOwnerType.Organization

  const managedBy = (owner: CopyrightOwnerItem) =>
    owner.owner && 'name' in owner.owner ? owner.owner.name : ''

  const fetchNextPage = () => {
    const next = result.value?.copyrightOwnersNew.next
    if (!next || loading.value) return

    fetchMore({
      variables: { next },
      updateQuery: (previousData, { fetchMoreResult: newData }) => {
        if (!newData) return previousData

        return {
          ...newData,
          copyrightOwnersNew: {
            ...newData.copyrightOwnersNew,
            items: [
              ...previousData.copyrightOwnersNew.items,
              ...newData.copyrightOwnersNew.items,
            ],
          },
        }
      },
    })
  }

  const handleScroll = (event: Event) => {
    const element = event.target as HTMLElement
    const scrollPos = element.clientHeight + element.scrollTop
    if (scrollPos / element.scrollHeight >= 0.8) fetchNextPage()
  }

  const handleNew = () =>
    openDialog(Dialog.NewCopyrightOwner, { stack: 'cover' })

  const handleEdit = (owner: CopyrightOwnerItem) =>
    openDialog(Dialog.EditCopyrightOwner, { props: { owner }, stack: 'cover' })

  const handleDelete = (owner: CopyrightOwnerItem) =>
    openModal(Modals.DeleteCopyrightOwner, { props: { owner }, stack: true })

  return {
    owners,
    isAccount,
    isOrganization,
    managedBy,
    handleScroll,
    handleNew,
    handleEdit,
    handleDelete,
  }
}
